import { useCallback, useId, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Input from "ds/components/Input";
import Typography from "ds/components/Typography";
import { AccessResult } from "utils/access";

import styles from "./styles.module.css";

type PriorityTagProps = {
  value: number;
  onChange: (value: number) => Promise<void>;
  onDetach: () => Promise<void>;
  canUpdate?: AccessResult;
};

type PriorityFormData = {
  priority: number;
};

const validatePriority = (value: number) => /^[0-9]+$/.test(value.toString());

const PriorityTag = ({ value, onChange, onDetach, canUpdate }: PriorityTagProps) => {
  const priorityInputId = useId();
  const [isDetaching, setIsDetaching] = useState(false);

  const onSubmit = useCallback(
    async (formData: PriorityFormData) => {
      if (value !== formData.priority) {
        await onChange(formData.priority);
      }
    },
    [onChange, value]
  );

  const form = useForm<PriorityFormData>({
    defaultValues: {
      priority: value,
    },
    mode: "onChange",
    shouldFocusError: false,
  });

  const { handleSubmit, register, formState } = form;

  const handleDetach = useCallback(async () => {
    setIsDetaching(true);

    await onDetach();

    setIsDetaching(false);
  }, [onDetach]);

  return (
    <Box align="center" gap="medium">
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box align="center" gap="medium">
            <Typography variant="p-t7" tag="label" htmlFor={priorityInputId}>
              Priority
            </Typography>

            <Input
              id={priorityInputId}
              error={!!formState.errors.priority}
              size="small"
              className={styles.input}
              disabled={formState.isSubmitting || !canUpdate?.result}
              {...register("priority", {
                valueAsNumber: true,
                min: 0,
                required: true,
                validate: validatePriority,
                onBlur: handleSubmit(onSubmit),
              })}
            />
          </Box>
        </form>
      </FormProvider>

      <Button
        variant="ghostDanger"
        size="small"
        onPress={handleDetach}
        disabled={isDetaching || !canUpdate?.result}
      >
        Detach
      </Button>
    </Box>
  );
};

export default PriorityTag;
